import React, { useEffect, useState } from "react";
import "./Banners.css";

import config from "../../../config";
import { getAuth, signInAnonymously } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase";

const BANNER_TYPES = ["DISCOUNT", "COLLECTION", "ANNOUNCEMENT"];
const PLACEMENTS = [
  "HOME",
  "LANDING",
  "CATEGORY_BOYS",
  "CATEGORY_GIRLS",
  "CATEGORY_MEN",
  "CATEGORY_LADIES",
];
const ACTION_TYPES = ["FILTER", "NONE"];

export default function CreateBannerModal({ onClose, onSuccess, initialData }) {
  const [form, setForm] = useState({
    title: initialData?.title || "",
    subtitle: initialData?.subtitle || "",
    imageUrl: initialData?.imageUrl || "",
    bannerType: initialData?.bannerType || "DISCOUNT",
    placement: initialData?.placement || "HOME",
    actionType: initialData?.actionType || "FILTER",
    priority: initialData?.priority || 10,
    active: initialData?.active ?? true,

    category: "",
    subCategory: "",
    sort: "latest",
  });

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
  if (!initialData) return;

  let payload = {};
  try {
    payload = initialData.actionPayload
      ? JSON.parse(initialData.actionPayload)
      : {};
  } catch (e) {}

  setForm({
    title: initialData.title || "",
    subtitle: initialData.subtitle || "",
    imageUrl: initialData.imageUrl || "",
    bannerType: initialData.bannerType || "DISCOUNT",
    placement: initialData.placement || "HOME",
    actionType: initialData.actionType || "FILTER",
    priority: initialData.priority ?? 10,
    active: initialData.active ?? true,

    category: payload.category || "",
    subCategory: payload.subCategory || "",
    sort: payload.sort || "latest",
  });
}, [initialData]);


  /* =========================
     HELPERS
     ========================= */

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /* =========================
     FETCH CATEGORIES
     ========================= */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/categories`);
        if (res.ok) {
          setCategories(await res.json());
        }
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    };
    fetchCategories();
  }, []);

  /* =========================
     FETCH SUB CATEGORIES
     ========================= */

  useEffect(() => {
    if (!form.category) {
      setSubCategories([]);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const encoded = encodeURIComponent(form.category);
        const res = await fetch(
          `${config.baseUrl}/api/subCategories/category/${encoded}`
        );
        if (res.ok) {
          setSubCategories(await res.json());
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        setSubCategories([]);
      }
    };

    fetchSubCategories();
  }, [form.category]);

  /* =========================
     FIREBASE IMAGE UPLOAD
     ========================= */

  const handleBannerImageUpload = async (file) => {
    if (!file) return;

    setUploadError("");
    setUploading(true);

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const userId = auth.currentUser.uid;
      const filename = `banner_${Date.now()}.jpg`;

      const storageRef = ref(
        storage,
        `banner-images/${userId}/${filename}`
      );

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange("imageUrl", url);
    } catch (err) {
      console.error("Banner upload failed", err);
      setUploadError("Failed to upload banner image.");
    } finally {
      setUploading(false);
    }
  };

  /* =========================
     SAVE BANNER
     ========================= */

  const handleSave = async () => {
   if (!form.title) {
  alert("Title is mandatory");
  return;
}

if (!initialData && !form.imageUrl) {
  alert("Banner image is mandatory");
  return;
}

    const payload =
      form.actionType === "FILTER"
        ? {
            category: form.category || null,
            subCategory: form.subCategory || null,
            sort: form.sort || null,
          }
        : null;

    const bannerData = {
      title: form.title,
      subtitle: form.subtitle,
      imageUrl: form.imageUrl,
      bannerType: form.bannerType,
      placement: form.placement,
      actionType: form.actionType,
      actionPayload: payload ? JSON.stringify(payload) : null,
      priority: form.priority,
      active: form.active,
    };

    try {
      const res = await fetch(
        initialData
          ? `${config.baseUrl}/admin/banners/${initialData.id}`
          : `${config.baseUrl}/admin/banners`,
        {
          method: initialData ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(bannerData),
        }
      );

      if (!res.ok) {
        throw new Error("Failed to save banner");
      }

      onSuccess && onSuccess();
    } catch (err) {
      alert("Error saving banner");
      console.error(err);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content large-modal">
        <div className="modal-header">
          <h3>{initialData ? "Edit Banner" : "Create New Banner"}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* IMAGE */}
          <div className="form-group">
            <label>Banner Image</label>
            <label className="upload-label">
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  handleBannerImageUpload(e.target.files?.[0])
                }
              />
              <div className="upload-box">
                {uploading ? "Uploading..." : "📤 Upload Banner Image"}
              </div>
            </label>

            {uploadError && <div className="error-message">{uploadError}</div>}

            {form.imageUrl && (
              <div className="banner-preview-frame">
                <img src={form.imageUrl} alt="Preview" />
                <small>Mobile preview (3:4)</small>
              </div>
            )}
          </div>

          {/* FORM */}
          <div className="form-grid">
            <div className="form-group">
              <label>Title</label>
              <input
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Subtitle</label>
              <input
                value={form.subtitle}
                onChange={(e) => handleChange("subtitle", e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Banner Type</label>
              <select
                value={form.bannerType}
                onChange={(e) =>
                  handleChange("bannerType", e.target.value)
                }
              >
                {BANNER_TYPES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Placement</label>
              <select
                value={form.placement}
                onChange={(e) =>
                  handleChange("placement", e.target.value)
                }
              >
                {PLACEMENTS.map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Action Type</label>
              <select
                value={form.actionType}
                onChange={(e) =>
                  handleChange("actionType", e.target.value)
                }
              >
                {ACTION_TYPES.map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <input
                type="number"
                value={form.priority}
                onChange={(e) =>
                  handleChange("priority", Number(e.target.value))
                }
              />
            </div>
          </div>

          {/* FILTER PAYLOAD */}
          {form.actionType === "FILTER" && (
            <div className="payload-box">
              <h4>Filter Settings</h4>

              <div className="form-grid">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      handleChange("category", e.target.value)
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c.categoryId} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Sub Category</label>
                  <select
                    value={form.subCategory}
                    disabled={!form.category}
                    onChange={(e) =>
                      handleChange("subCategory", e.target.value)
                    }
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.map((sc) => (
                      <option
                        key={sc.subCategoryId}
                        value={sc.subCategoryName}
                      >
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Sort</label>
                  <select
                    value={form.sort}
                    onChange={(e) =>
                      handleChange("sort", e.target.value)
                    }
                  >
                    <option value="latest">Latest</option>
                    <option value="price_low_high">
                      Price Low → High
                    </option>
                    <option value="price_high_low">
                      Price High → Low
                    </option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ACTIVE */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  handleChange("active", e.target.checked)
                }
              />
              <span>{form.active ? "Active" : "Inactive"}</span>
            </label>
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={uploading}
          >
            Save Banner
          </button>
        </div>
      </div>
    </div>
  );
}
